import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { Plus, Search, Edit, Trash2, Camera, Database } from "lucide-react";
import { ColDef, themeQuartz } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { toast } from "react-toastify";
import { SpinLoader } from "../../../components/loader/Loader";
import Modal from "../../../components/modal/Modal";
import { Dropdown } from "../../../components/dropdown/Dropdown";
import {
  getAllCameras,
  deleteCamera,
  getAllAccessZones,
  seedSampleCameras,
} from "../../../service/securityService";
import {
  Camera as CameraType,
  SecuritySearchFilters,
  AccessZone,
} from "../../../types/security_types";
import CameraForm from "./CameraForm";

const gridTheme = themeQuartz.withParams({
  backgroundColor: "#fff",
  foregroundColor: "#2a2a2a",
  headerTextColor: "#2a2a2a",
  headerBackgroundColor: "#E9F5FF",
  oddRowBackgroundColor: "rgb(0, 0, 0, 0.03)",
  headerColumnResizeHandleColor: "rgb(126, 46, 132)",
  fontSize: "0.75rem",
  headerFontWeight: "bold",
});

const Cameras: React.FC = () => {
  const [cameras, setCameras] = useState<CameraType[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedZone, setSelectedZone] = useState<string>("");
  const [zones, setZones] = useState<AccessZone[]>([]);
  const [seeding, setSeeding] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedCamera, setSelectedCamera] = useState<CameraType | null>(null);

  useEffect(() => {
    getAllAccessZones()
      .then(setZones)
      .catch(() => setZones([]));
  }, []);

  useEffect(() => {
    fetchCameras();
  }, [searchQuery, selectedZone]);

  const fetchCameras = async () => {
    try {
      setLoading(true);
      const filters: SecuritySearchFilters = {};
      if (selectedZone) filters.zoneId = selectedZone;
      if (searchQuery.trim()) filters.searchQuery = searchQuery;

      const data = await getAllCameras({ ...filters, limit: 500 });
      setCameras(data);
    } catch (error: any) {
      console.error("Error fetching cameras:", error);
      toast.error(error.response?.data?.resMessage || "Failed to load cameras");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setSelectedCamera(null);
    setIsCreateModalOpen(true);
  };

  const handleEdit = (camera: CameraType) => {
    setSelectedCamera(camera);
    setIsEditModalOpen(true);
  };

  const handleDelete = async (camera: CameraType) => {
    if (!window.confirm(`Are you sure you want to delete "${camera.cameraName}"?`)) {
      return;
    }

    try {
      const id = camera._id || camera.id;
      if (!id) return;
      await deleteCamera(id);
      toast.success("Camera deleted successfully");
      fetchCameras();
    } catch (error: any) {
      console.error("Error deleting camera:", error);
      toast.error(error.response?.data?.resMessage || "Failed to delete camera");
    }
  };

  const handleSeedSamples = async () => {
    if (!window.confirm("Add sample cameras ICU_CAM_01 and ENTRY_CAM_02 if they are missing?")) {
      return;
    }
    try {
      setSeeding(true);
      const { inserted } = await seedSampleCameras();
      toast.success(
        inserted > 0 ? `Added ${inserted} sample camera(s).` : "Samples already present."
      );
      fetchCameras();
    } catch (e: any) {
      console.error(e);
      toast.error(e.response?.data?.message || "Could not seed samples");
    } finally {
      setSeeding(false);
    }
  };

  const handleFormSuccess = () => {
    setIsCreateModalOpen(false);
    setIsEditModalOpen(false);
    setSelectedCamera(null);
    fetchCameras();
  };

  const filteredCameras = useMemo(() => {
    if (!searchQuery) return cameras;
    const query = searchQuery.toLowerCase();
    return cameras.filter(
      (camera) =>
        camera.cameraName?.toLowerCase().includes(query) ||
        camera.cameraId?.toLowerCase().includes(query) ||
        camera.ipAddress?.toLowerCase().includes(query) ||
        camera.location?.toLowerCase().includes(query)
    );
  }, [cameras, searchQuery]);

  const columnDefs = useMemo<ColDef[]>(
    () => [
      {
        field: "cameraId",
        headerName: "Camera ID",
        sortable: true,
        minWidth: 150,
        flex: 1,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
        cellStyle: () => ({
          padding: "10px 5px 0 10px",
          textAlign: "left",
        }),
      },
      {
        field: "cameraName",
        headerName: "Camera Name",
        sortable: true,
        minWidth: 200,
        flex: 1.5,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
        cellStyle: () => ({
          padding: "10px 5px 0 10px",
          textAlign: "left",
        }),
      },
      {
        field: "location",
        headerName: "Location",
        sortable: true,
        minWidth: 150,
        flex: 1,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
        cellStyle: () => ({
          padding: "10px 5px 0 10px",
          textAlign: "left",
        }),
        valueGetter: (params) => params.data?.location || "-",
      },
      {
        field: "cameraType",
        headerName: "Type",
        sortable: true,
        minWidth: 100,
        flex: 1,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
        cellStyle: () => ({
          padding: "10px 5px 0 10px",
          textAlign: "left",
        }),
        valueFormatter: (p) => {
          const v = p.value as string;
          if (v === "DOME") return "Dome";
          if (v === "PTZ") return "PTZ";
          if (v === "IP") return "IP";
          return v || "—";
        },
      },
      {
        field: "vendor",
        headerName: "Vendor",
        sortable: true,
        minWidth: 110,
        flex: 1,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
        valueGetter: (params) => params.data?.vendor || "—",
      },
      {
        field: "healthStatus",
        headerName: "Health",
        sortable: true,
        minWidth: 100,
        flex: 1,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
        valueGetter: (params) => params.data?.healthStatus || "Unknown",
      },
      {
        field: "ipAddress",
        headerName: "IP Address",
        sortable: true,
        minWidth: 130,
        flex: 1,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
        cellStyle: () => ({
          padding: "10px 5px 0 10px",
          textAlign: "left",
        }),
      },
      {
        field: "status",
        headerName: "Status",
        sortable: true,
        minWidth: 120,
        flex: 1,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
        cellStyle: (params) => {
          const status = params.value;
          return {
            padding: "10px 5px 0 10px",
            textAlign: "left",
            color: status === "Active" ? "#10b981" : status === "Maintenance" ? "#f59e0b" : "#6b7280",
            fontWeight: "500",
          };
        },
        cellRenderer: (params: any) => {
          const status = params.value;
          const colors: Record<string, string> = {
            Active: "bg-green-100 text-green-800",
            Maintenance: "bg-yellow-100 text-yellow-800",
            Inactive: "bg-gray-100 text-gray-800",
          };
          return (
            <span
              className={`px-2 py-1 rounded-full text-xs font-semibold ${colors[status] || "bg-gray-100 text-gray-800"
                }`}
            >
              {status}
            </span>
          );
        },
      },
      {
        field: "live",
        headerName: "Live",
        sortable: false,
        minWidth: 70,
        flex: 0.6,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
        cellRenderer: (params: { data?: CameraType }) => {
          const id = params.data?._id || params.data?.id;
          if (!id) return null;
          return (
            <Link
              to={`/security/surveillance/live?id=${id}`}
              className="text-[#0fa9a6] text-xs font-semibold hover:underline"
            >
              View
            </Link>
          );
        },
      },
      {
        field: "actions",
        headerName: "Actions",
        sortable: false,
        minWidth: 150,
        flex: 1,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
        cellRenderer: (params: any) => {
          return (
            <div className="flex items-center gap-2 h-full">
              <button
                onClick={() => handleEdit(params.data)}
                className="text-[#0fa9a6] hover:text-[#008f8c] p-1 rounded transition-colors"
                title="Edit"
              >
                <Edit className="w-4 h-4" />
              </button>
              <button
                onClick={() => handleDelete(params.data)}
                className="text-red-500 hover:text-red-700 p-1 rounded transition-colors"
                title="Delete"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          );
        },
      },
    ],
    []
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      resizable: true,
    }),
    []
  );

  const theme = useMemo(() => gridTheme, []);

  return (
    <>
      <div className="px-2 py-4 mt-14 min-h-screen">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-3">
            <div className="bg-[#EAFBFA] p-3 rounded-lg">
              <Camera className="w-6 h-6 text-[#0fa9a6]" />
            </div>
            <div>
              <h2 className="ml-2">Camera Management</h2>
              <p className="text-sm text-gray-600 ml-2">Manage CCTV cameras and surveillance systems</p>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button
              type="button"
              className="flex items-center justify-center font-semibold gap-2 bg-white text-[#0fa9a6] border border-[#0fa9a6] px-4 py-2 rounded-full hover:bg-gray-50 disabled:opacity-50"
              onClick={handleSeedSamples}
              disabled={seeding}
            >
              <Database size={18} /> Sample data
            </button>
            <button
              className="flex items-center justify-center font-semibold gap-2 bg-[#0fa9a6] text-white border border-[#0fa9a6] px-4 py-2 rounded-full hover:bg-teal-600"
              onClick={handleCreate}
            >
              <Plus size={20} /> Add Camera
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="mb-4 flex gap-4 items-center flex-wrap">
          <select
            value={selectedZone}
            onChange={(e) => setSelectedZone(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm min-w-[180px] bg-white"
          >
            <option value="">All zones</option>
            {zones.map((z) => (
              <option key={z._id || z.id} value={z._id || z.id}>
                {z.zoneName}
              </option>
            ))}
          </select>
          <div className="relative flex-1 min-w-[200px]">
            <input
              type="text"
              placeholder="Search cameras..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#375998] text-sm"
            />
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          </div>
        </div>

        <div className="tableBlock w-full">
          <div className="card">
            {filteredCameras?.length > 0 ? (
              <div className="ag-theme-alpine w-full min-h-[400px] border-0 rounded-lg shadow-lg">
                <AgGridReact
                  rowData={filteredCameras}
                  columnDefs={columnDefs}
                  theme={theme}
                  headerHeight={65}
                  rowHeight={50}
                  loading={loading}
                  defaultColDef={defaultColDef}
                  domLayout="autoHeight"
                  pagination={true}
                  paginationPageSize={10}
                  paginationPageSizeSelector={[5, 10, 20]}
                  paginationAutoPageSize={false}
                />
              </div>
            ) : (
              <div className="flex flex-col justify-center items-center h-64">
                <Camera className="w-16 h-16 mb-4 text-gray-300" />
                <p className="text-gray-500 text-lg mb-4">No cameras found</p>
                <p className="text-sm text-gray-400">
                  {searchQuery ? "Try adjusting your search" : "Add your first camera"}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create Modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => {
          setIsCreateModalOpen(false);
          setSelectedCamera(null);
        }}
        closeButton={false}
        modalClass="bg-white w-full max-w-3xl rounded-xl shadow-lg relative flex flex-col max-h-[80vh]"
      >
        <div className="pt-6 overflow-y-auto flex-1">
          <CameraForm
            onSuccess={handleFormSuccess}
            onCancel={() => {
              setIsCreateModalOpen(false);
              setSelectedCamera(null);
            }}
          />
        </div>
        <div className="h-12" />
        <div className="flex justify-end gap-3 p-3 bg-[#5A7192] w-full rounded-b-lg">
          <button
            type="button"
            className="px-4 py-2 bg-[#8497B0] text-white rounded-full hover:bg-teal-600 transition"
            onClick={() => {
              setIsCreateModalOpen(false);
              setSelectedCamera(null);
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="cameraForm"
            className="px-4 py-2 bg-[#00A8A8] text-white rounded-full hover:bg-teal-600 transition"
          >
            Create
          </button>
        </div>
      </Modal>

      {/* Edit Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedCamera(null);
        }}
        closeButton={false}
        modalClass="bg-white w-full max-w-3xl rounded-xl shadow-lg relative flex flex-col max-h-[80vh]"
      >
        <div className="pt-6 overflow-y-auto flex-1">
          <CameraForm
            camera={selectedCamera || undefined}
            onSuccess={handleFormSuccess}
            onCancel={() => {
              setIsEditModalOpen(false);
              setSelectedCamera(null);
            }}
          />
        </div>
        <div className="h-12" />
        <div className="flex justify-end gap-3 p-3 bg-[#5A7192] w-full rounded-b-lg">
          <button
            type="button"
            className="px-4 py-2 bg-[#8497B0] text-white rounded-full hover:bg-teal-600 transition"
            onClick={() => {
              setIsEditModalOpen(false);
              setSelectedCamera(null);
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="cameraForm"
            className="px-4 py-2 bg-[#00A8A8] text-white rounded-full hover:bg-teal-600 transition"
          >
            Update
          </button>
        </div>
      </Modal>
    </>
  );
};

export default Cameras;
